import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { companyInfo, heroData, aboutData, servicesData, whyChooseUsData, projectsData } from '@/mocks/home';
import HappyClientsSection from './sections/HappyClientsSection';
import { getWhatsAppUrl, getGenericWhatsAppUrl } from '@/utils/whatsapp';
import { useScrollAnimation, useCountUp } from '@/hooks/useScrollAnimation';
import JsonLd, { getOrganizationSchema, getWebPageSchema, getBreadcrumbSchema } from '@/utils/seo';

const featuredServiceSlugs = ['landscaping', 'upvc-windows', 'interior-works', 'civil-waterproofing', 'deep-cleaning'];

const avatarStyles = [
  { bg: 'bg-primary-50', icon: 'text-primary-500' },
  { bg: 'bg-accent-50', icon: 'text-accent-500' },
  { bg: 'bg-secondary-50', icon: 'text-secondary-500' },
];
const getAvatarStyle = (id: number) => avatarStyles[(id - 1) % 3];

function StatCounter({ value, suffix, label, isVisible }: { value: number; suffix: string; label: string; isVisible: boolean }) {
  const count = useCountUp(value, 2000, isVisible);
  return (
    <div className="text-center">
      <div className="font-heading font-bold text-3xl md:text-4xl text-gradient">
        {count}{suffix}
      </div>
      <div className="text-sm text-foreground-600 mt-1">{label}</div>
    </div>
  );
}

export default function HomePage() {
  const { ref: aboutRef, isVisible: aboutVisible } = useScrollAnimation(0.08);
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation(0.08);
  const { ref: servicesRef, isVisible: servicesVisible } = useScrollAnimation(0.05);
  const { ref: whyUsRef, isVisible: whyUsVisible } = useScrollAnimation(0.05);
  const { ref: projectsRef, isVisible: projectsVisible } = useScrollAnimation(0.05);
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation(0.08);

  // Logo carousel section handles its own state

  return (
    <Layout>
      <JsonLd data={getOrganizationSchema()} />
      <JsonLd data={getWebPageSchema('Infinite Consultant & Services | Transforming Spaces, Enhancing Lives', 'Complete property solutions in Bangalore — Landscaping, Interiors, Construction, Fabrication, Cleaning, Waterproofing, Branding and Annual Maintenance Contracts.', 'https://infiniteconsultant.com/')} />
      <JsonLd data={getBreadcrumbSchema([{ name: 'Home', url: 'https://infiniteconsultant.com/' }])} />
      {/* Hero Banner */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Premium%20Indian%20property%20development%20aerial%20view%20Bangalore%20luxury%20villa%20landscaping%20modern%20construction%20green%20gardens%20contemporary%20South%20Indian%20architecture%20warm%20golden%20hour%20professional%20photography&width=1920&height=1080&seq=hero-bg-1&orientation=landscape&nocache=true"
            alt="Premium property solutions"
            className="w-full h-full object-cover animate-reveal"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/50 to-foreground-950/80" />
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10 pt-20 pb-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6 animate-fade-in" style={{ animationDuration: '0.8s' }}>
              <span className="w-2 h-2 rounded-full bg-secondary-400" style={{ animation: 'breathe 2s ease-in-out infinite' }} />
              <span className="text-white/90 text-sm font-medium">Trusted by 500+ Clients across Bangalore</span>
            </div>

            <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-4" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.05s forwards' }}>
              {heroData.headline}
            </h1>

            <h2 className="font-heading font-medium text-lg sm:text-xl md:text-2xl text-white/90 mb-4" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.2s forwards' }}>
              {heroData.subHeading}
            </h2>

            <p className="text-sm sm:text-base md:text-lg text-white/75 max-w-2xl mx-auto mb-8 leading-relaxed" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.35s forwards' }}>
              {heroData.description}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.5s forwards' }}>
              <Link
                to="/contact"
                className="btn-ripple w-full sm:w-auto px-8 py-3.5 rounded-lg bg-gradient-brand text-white font-medium text-sm hover:bg-gradient-brand-hover transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
              >
                {heroData.cta1}
              </Link>
              <a
                href={`tel:${companyInfo.phone1.replace(/\s/g, '')}`}
                className="w-full sm:w-auto px-8 py-3.5 rounded-lg bg-white text-foreground-950 font-medium text-sm hover:bg-white/90 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2 text-primary-500" />
                {heroData.cta2}
              </a>
              <a
                href={getGenericWhatsAppUrl()}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-full sm:w-auto px-8 py-3.5 rounded-lg bg-secondary-500 text-white font-medium text-sm hover:bg-secondary-600 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-2" />
                {heroData.cta3}
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent" />
      </section>

      <HappyClientsSection />

      {/* About Company Summary */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <div ref={aboutRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center transition-all duration-700 ${aboutVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20Indian%20construction%20team%20Bangalore%20site%20supervisors%20and%20workers%20in%20hard%20hats%20safety%20vests%20reviewing%20plans%20at%20luxury%20residential%20project%20warm%20daylight%20Indian%20corporate%20photography&width=800&height=700&seq=about-img-1&orientation=landscape&nocache=true"
                    alt="Infinite Consultant team at work"
                    className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-700 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/30 to-transparent" />
                </div>
              </div>

              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200/70 mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                  <span className="text-xs font-medium text-primary-700 uppercase tracking-wider">About Us</span>
                </div>

                <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
                  {aboutData.subtitle}
                </h2>

                <div className="space-y-4 mb-8">
                  {aboutData.paragraphs.map((paragraph, index) => (
                    <p key={index} className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>

                <Link
                  to="/about"
                  className="btn-ripple inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-brand text-white font-medium text-sm hover:bg-gradient-brand-hover transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
                >
                  Learn More About Us
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line" />
                  </div>
                </Link>
              </div>
            </div>

            <div ref={statsRef} className={`mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 transition-all duration-700 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {aboutData.stats.map((stat, index) => (
                <StatCounter key={index} value={parseInt(stat.value) || 0} suffix={stat.suffix} label={stat.label} isVisible={statsVisible} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div ref={servicesRef} className={`max-w-6xl mx-auto transition-all duration-700 ${servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200/70 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                <span className="text-xs font-medium text-primary-700 uppercase tracking-wider">What We Do</span>
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
                Our Services
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                Comprehensive property solutions tailored to your needs — from landscaping to maintenance, we handle it all.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {servicesData.slice(0, 6).map((service, index) => {
                const hasDetail = featuredServiceSlugs.includes(
                  ['landscaping', 'interior-works', 'upvc-windows', 'wpc-paneling', 'branding', 'fabrication', 'electrical-plumbing', 'civil-waterproofing', 'deep-cleaning', 'amc'][index]
                );
                return (
                  <div
                    key={index}
                    className={`group bg-white rounded-xl border border-background-200/70 overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-sm ${servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: `${index * 0.08}s` }}
                  >
                    <div className="h-1 bg-gradient-to-r from-primary-500 to-secondary-500 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                    <div className="p-5 md:p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary-50 flex items-center justify-center shrink-0 group-hover:bg-primary-500 transition-colors duration-300">
                          <i className={`${service.icon} text-primary-500 text-lg md:text-xl group-hover:text-white transition-colors duration-300`} />
                        </div>
                        <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 leading-tight">
                          {service.category}
                        </h3>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {service.items.slice(0, 3).map((item, idx) => (
                          <span
                            key={idx}
                            className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-background-100 text-foreground-600 border border-background-200/50"
                          >
                            {item}
                          </span>
                        ))}
                        {service.items.length > 3 && (
                          <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium text-foreground-400">
                            +{service.items.length - 3} more
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {hasDetail ? (
                          <>
                            <Link
                              to={`/services/${featuredServiceSlugs[index]}`}
                              className="flex-1 px-4 py-2 rounded-lg bg-primary-50 text-primary-600 text-xs font-medium text-center hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              Learn More
                              <div className="w-3 h-3 inline-flex items-center justify-center ml-1">
                                <i className="ri-arrow-right-line text-xs" />
                              </div>
                            </Link>
                            <a
                              href={getWhatsAppUrl(service.category)}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="px-3 py-2 rounded-lg bg-secondary-50 text-secondary-600 text-xs font-medium hover:bg-secondary-100 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <i className="ri-whatsapp-line" />
                            </a>
                          </>
                        ) : (
                          <Link
                            to="/services"
                            className="flex-1 px-4 py-2 rounded-lg bg-primary-50 text-primary-600 text-xs font-medium text-center hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            View All Services
                            <div className="w-3 h-3 inline-flex items-center justify-center ml-1">
                              <i className="ri-arrow-right-line text-xs" />
                            </div>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-foreground-800 font-medium text-sm border border-background-200/70 hover:border-primary-300 hover:text-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                View All Services
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div ref={whyUsRef} className={`max-w-6xl mx-auto transition-all duration-700 ${whyUsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary-50 border border-secondary-200/70 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary-500" />
                <span className="text-xs font-medium text-secondary-700 uppercase tracking-wider">Why Us</span>
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
                Why Choose Us
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                We combine expertise, quality, and dedication to deliver results that exceed expectations every time.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
              {whyChooseUsData.slice(0, 10).map((item, index) => (
                <div
                  key={index}
                  className={`group bg-white rounded-xl p-5 md:p-6 border border-background-200/70 text-center hover:shadow-sm transition-all duration-500 hover:-translate-y-1 ${whyUsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${index * 0.06}s` }}
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-3 rounded-xl bg-gradient-brand flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <i className={`${item.icon} text-white text-xl md:text-2xl`} />
                  </div>
                  <h3 className="font-heading font-medium text-sm text-foreground-950">
                    {item.title}
                  </h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects Preview */}
      <section className="py-16 md:py-24 bg-background-100">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div ref={projectsRef} className={`max-w-6xl mx-auto transition-all duration-700 ${projectsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="text-center mb-10 md:mb-12">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-50 border border-primary-200/70 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                <span className="text-xs font-medium text-primary-700 uppercase tracking-wider">Portfolio</span>
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4">
                Featured Projects
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                Browse through our completed projects showcasing quality craftsmanship across all service categories.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {projectsData.slice(0, 6).map((project, index) => (
                <div
                  key={project.id}
                  className={`group bg-white rounded-xl overflow-hidden border border-background-200/70 hover:shadow-sm transition-all duration-500 ${projectsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-52 md:h-60 object-cover object-top group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 rounded-md bg-white/90 backdrop-blur-sm text-xs font-medium text-foreground-800">
                        {project.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading font-semibold text-base text-foreground-950">
                      {project.title}
                    </h3>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-foreground-800 font-medium text-sm border border-background-200/70 hover:border-primary-300 hover:text-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                View All Projects
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section ref={ctaRef} className={`py-16 md:py-20 bg-gradient-brand transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-white mb-4">
              Ready to Transform Your Space?
            </h2>
            <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto mb-8">
              Get free consultation and quotation. We are just a call or message away.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                className="btn-ripple w-full sm:w-auto px-8 py-3.5 rounded-lg bg-white text-primary-600 font-medium text-sm hover:bg-background-50 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
              >
                Get Free Quotation
                <div className="w-4 h-4 inline-flex items-center justify-center ml-2">
                  <i className="ri-arrow-right-line" />
                </div>
              </Link>
              <a
                href={getGenericWhatsAppUrl()}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-full sm:w-auto px-8 py-3.5 rounded-lg bg-white/10 text-white border border-white/20 font-medium text-sm hover:bg-white/20 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-2" />
                WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}